const asLocation = (locationLike) => {
  if (!locationLike) return null;
  try {
    const href = locationLike.href || String(locationLike);
    return new URL(href);
  } catch (_) {
    return null;
  }
};

export const isSameOriginUrl = (url, locationLike) => {
  if (!url) return true;
  const location = asLocation(locationLike);
  if (!location) return false;

  try {
    return new URL(url, location.href).origin === location.origin;
  } catch (_) {
    return false;
  }
};

export const readAccessibleStyleRules = (styleSheet, locationLike) => {
  if (!styleSheet || !isSameOriginUrl(styleSheet.href, locationLike)) return [];

  try {
    return Array.from(styleSheet.cssRules || []);
  } catch (_) {
    // Same-origin URLs can still be opaque because of redirects or CORS.
    return [];
  }
};

export const collectAccessibleFontCss = (styleSheets, locationLike) =>
  Array.from(styleSheets || [])
    .flatMap((styleSheet) =>
      readAccessibleStyleRules(styleSheet, locationLike)
    )
    .filter(
      (rule) =>
        rule?.type === 5 || /^\s*@font-face\b/i.test(rule?.cssText || '')
    )
    .map((rule) => rule.cssText)
    .filter(Boolean)
    .join('\n');

const shouldIncludeNode = (node, locationLike) => {
  if (
    node?.tagName === 'LINK' &&
    String(node.getAttribute?.('rel') || '').toLowerCase() === 'stylesheet'
  ) {
    return isSameOriginUrl(node.href || node.getAttribute?.('href'), locationLike);
  }
  return true;
};

export const createExportImageOptions = ({
  filter,
  locationLike = globalThis.document?.location,
  styleSheets = globalThis.document?.styleSheets,
} = {}) => ({
  // Supplying guarded font CSS prevents html-to-image from enumerating every
  // document stylesheet itself. Computed same-origin styles remain cloned.
  fontEmbedCSS: collectAccessibleFontCss(styleSheets, locationLike),
  filter: (node) =>
    shouldIncludeNode(node, locationLike) && (filter ? filter(node) : true),
});

export const captureExportPng = async (
  node,
  toPngImpl,
  options = {}
) => {
  if (!node || typeof toPngImpl !== 'function') return null;

  try {
    const dataUrl = await toPngImpl(
      node,
      createExportImageOptions(options)
    );
    return typeof dataUrl === 'string' && dataUrl.startsWith('data:image/')
      ? dataUrl
      : null;
  } catch (_) {
    return null;
  }
};

export const createProposalExportCaptureError = (
  assetName = 'export image',
  cause = null
) => {
  const error = new Error(
    `Required export image "${assetName}" could not be captured.`
  );
  error.name = 'ProposalExportCaptureError';
  error.assetName = assetName;
  if (cause) error.cause = cause;
  return error;
};

export const requireExportImageDataUrl = (
  dataUrl,
  { assetName = 'export image', cause = null } = {}
) => {
  if (typeof dataUrl === 'string' && dataUrl.startsWith('data:image/')) {
    return dataUrl;
  }

  throw createProposalExportCaptureError(assetName, cause);
};

export const captureRequiredExportPng = async (
  node,
  toPngImpl,
  { assetName = 'export image', ...options } = {}
) => {
  const dataUrl = await captureExportPng(node, toPngImpl, options);
  return requireExportImageDataUrl(dataUrl, { assetName });
};

export const captureRequiredCanvasExportImage = async (
  canvas3D,
  {
    assetName = 'rgbImage',
    fetchBlob,
    blobToDataURL,
  } = {}
) => {
  try {
    if (!canvas3D || typeof canvas3D.exportToJPG !== 'function') {
      throw new Error('Canvas3D exportToJPG is unavailable.');
    }
    if (typeof fetchBlob !== 'function' || typeof blobToDataURL !== 'function') {
      throw new Error('Canvas3D export converters are unavailable.');
    }

    const imageData = await canvas3D.exportToJPG();
    if (!imageData?.url) {
      throw new Error('Canvas3D export did not return an image URL.');
    }

    const blob = await fetchBlob(imageData.url);
    const dataUrl = await blobToDataURL(blob);
    return requireExportImageDataUrl(dataUrl, { assetName });
  } catch (error) {
    if (error?.name === 'ProposalExportCaptureError') throw error;
    throw createProposalExportCaptureError(assetName, error);
  }
};
